import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Message, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { DocumentTypeSummary } from 'src/app/demo/models/common-data-types';
import { LanguageService } from 'src/app/demo/service/language.service';
import { TooltipModule } from 'primeng/tooltip';
import { formatDate } from 'src/app/demo/utilities/driver-utils';
import { CustomToastrService } from 'src/app/demo/service/custom-toastr.service';

@Component({
    selector: 'app-show-outstanding-documents',
    standalone: true,
    imports: [
        CommonModule,
        FileUploadModule,
        ButtonModule,
        MessagesModule,
        CalendarModule,
        TranslateModule,
        TooltipModule,
        FormsModule,
        ReactiveFormsModule,
        CardModule,
        MessagesModule,
        DropdownModule,
        InputTextModule,
    ],
    templateUrl: './show-outstanding-documents.component.html',
    styleUrls: ['./show-outstanding-documents.component.scss'],
    providers: [
        MessageService,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShowOutstandingDocumentsComponent),
            multi: true,
        },
    ],
})
export class ShowOutstandingDocumentsComponent
    implements OnInit, ControlValueAccessor
{
    @Input()
    outstandingDocuments: DocumentTypeSummary[] = [];

    @Input() ownerId!: string;

    @Input() identifier!: string;

    @Output() onUploadDocument = new EventEmitter<FormData>();

    documentForm!: FormGroup;
    selectedFile: File | undefined;
    messages: Message[] = [];
    selectedLanguage: any = null;

    private onChange: any = () => {};
    private onTouched: any = () => {};

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private languageService: LanguageService,
        private translate: TranslateService,
        private toastService: CustomToastrService,
    ) {}

    ngOnInit() {
        this.initializeDocumentForm();
        this.initializeLocale();
    }

    initializeDocumentForm() {
        this.documentForm = this.fb.group({
            file: [],
            documentMetadata: this.fb.group({
                ownerId: [this.ownerId],
                documentTypeId: [''],
                expiryDate: [''],
                documentIdentifier: [''],
            }),
        });
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translate.use(language);
        this.languageService.setLanguage(language);
    }

    translateText(input: string): string {
        let translatedText = '';
        this.translate.get(input).subscribe((outputText) => {
            translatedText = outputText;
        });

        return translatedText;
    }

    writeValue(value: any): void {
        if (value) {
            this.documentForm.get('file')?.setValue(value);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.documentForm.disable() : this.documentForm.enable();
    }

    onFileClear() {
        this.documentForm.get('file')?.setValue(null);
        this.onChange(null);
    }

    setDocumentType(value: string) {
        const form = this.documentForm.get('documentMetadata') as FormGroup;
        form.get('documentTypeId')?.setValue(value);
    }

    validateFormInputs(document: DocumentTypeSummary): boolean {
        this.setDocumentType(document.id);
        const documentMetadata = this.documentForm.get(
            'documentMetadata'
        ) as FormGroup;
        const expiry = documentMetadata.get('expiryDate');
        const identifier = documentMetadata.get('documentIdentifier');

        if (document.requiresExpiryDate && !expiry?.value) {
            this.showErrorToast('Expiry Date is required');
            return false;
        }
        if (document.requiresIdentifier && !identifier?.value) {
            this.showErrorToast('Document number is required');
            return false;
        }
        return true;
    }

    onFileSelect(event: any, document: any) {
        this.selectedFile = event.files[0];
        this.documentForm.get('file')?.setValue(this.selectedFile);
        this.onChange(this.selectedFile);
    }

    onUpload(event: FileUploadHandlerEvent, document: any) {
        this.validateDataAndEmit(event, document);
    }

    validateDataAndEmit(event: FileUploadHandlerEvent, document: any) {
        if (this.validateFormInputs(document)) {
            const documentMetadata = this.documentForm.get(
                'documentMetadata'
            ) as FormGroup;
            const formattedMetadata = {
                expiryDate: formatDate(
                    documentMetadata.get('expiryDate')?.value
                ),
                documentIdentifier:
                    documentMetadata.get('documentIdentifier')?.value,
                documentTypeId: documentMetadata.get('documentTypeId')?.value,
                ownerId: this.ownerId,
            };
            const file = event.files[0];
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append(
                'metadata',
                new Blob([JSON.stringify(formattedMetadata)], {
                    type: 'application/json',
                })
            );
            this.documentForm.reset();
            this.onUploadDocument.emit(formData);
        }
    }

    showSuccessToast(message: string) {
        this.toastService.showSuccess(message)
    }

    showErrorToast(message: string) {
        this.toastService.showError(message);
    }
}
